<template>
  <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-10 mx-auto p-5 border max-w-4xl shadow-lg rounded-md bg-white">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm md:text-lg font-medium text-gray-900 font-semibold">
            <span v-if="selectedSubject">
              Documentos académicos de "{{ formatSubject(selectedSubject.subject) }}"
            </span>
            <span v-else>
              Documentos Académicos
            </span>
          </h3>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Lista de documentos -->
            <div class="mb-4">
            <h4 class="text-md font-semibold mb-3">Documentos disponibles:</h4>
            <div v-if="loadingDocuments" class="flex justify-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            
            <div v-else-if="documentsError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {{ documentsError }}
            </div>
            
            <div v-else-if="availableDocuments && availableDocuments.length > 0">
                <!-- Vista móvil: tarjetas -->
                <div class="grid grid-cols-1 gap-3 sm:hidden">
                    <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="py-1 pl-4 pr-3 text-left text-xs font-small uppercase tracking-wide text-gray-500">
                            Documento
                            </th>
                            <th scope="col" class="px-1 py-3 text-center text-xs font-small uppercase tracking-wide text-gray-500">
                            Acciones
                            </th>
                        </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                        <tr v-for="document in availableDocuments" :key="document.iddocument" class="hover:bg-gray-50">
                            <td class="whitespace-nowrap py-1 pl-4 pr-3 text-sm font-small text-gray-900">
                            {{ document.document }}
                            </td>
                            <td class="whitespace-nowrap px-3 py-1 text-center">
                            <div class="flex justify-center space-x-2">
                                <div class="tooltip-wrapper">
                                <button 
                                    @click="viewDocument(document.iddocument, document.document)"
                                    :disabled="loadingDocument"
                                    class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                    </svg>
                                </button>
                                <div class="tooltip tooltip-details">
                                    <span class="tooltip-text">Ver documento</span>
                                    <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                    <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                                    </svg>
                                </div>
                                </div>
                                
                                <div class="tooltip-wrapper">
                                <button 
                                    @click="downloadDocument(document.iddocument, document.document)"
                                    :disabled="loadingDocument"
                                    class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                    </svg>
                                </button>
                                <div class="tooltip tooltip-details">
                                    <span class="tooltip-text">Descargar</span>
                                    <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                    <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                                    </svg>
                                </div>
                                </div>
                            </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                
                <!-- Vista escritorio: tabla -->
                <div class="hidden sm:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table class="min-w-full divide-y divide-gray-300">
                    <thead class="bg-gray-50">
                    <tr>
                        <th scope="col" class="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Documento
                        </th>
                        <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">
                        Acciones
                        </th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="document in availableDocuments" :key="document.iddocument" class="hover:bg-gray-50">
                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                        {{ document.document }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-center">
                        <div class="flex justify-center space-x-2">
                            <div class="tooltip-wrapper">
                            <button 
                                @click="viewDocument(document.iddocument, document.document)"
                                :disabled="loadingDocument"
                                class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                </svg>
                            </button>
                            <div class="tooltip tooltip-details">
                                <span class="tooltip-text">Ver documento</span>
                                <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                                </svg>
                            </div>
                            </div>
                            
                            <div class="tooltip-wrapper">
                            <button 
                                @click="downloadDocument(document.iddocument, document.document)"
                                :disabled="loadingDocument"
                                class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100"
                            >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                            </button>
                            <div class="tooltip tooltip-details">
                                <span class="tooltip-text">Descargar</span>
                                <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                                <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                                </svg>
                            </div>
                            </div>
                        </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
                </div>
            </div>
            </div>

            <!-- Visor de documentos -->
            <div v-if="viewingDocument" class="mb-4">
            <div class="flex items-center justify-between">
                <h4 class="text-md font-semibold mb-3">Vista previa: {{ currentDocumentName }}</h4>
                <button
                @click="closeViewer"
                class="text-gray-400 hover:text-gray-600"
                >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
            </div>
            
            <div v-if="loadingViewer" class="flex justify-center py-8 bg-gray-50 rounded">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            
            <div v-else-if="viewerError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {{ viewerError }}
                <div class="mt-2">
                <button
                    @click="openInNewTab"
                    class="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                    Abrir en nueva pestaña
                </button>
                </div>
            </div>
            
            <div v-else class="border rounded bg-white">
                <!-- Visor PDF optimizado -->
                <div class="relative">
                <!-- Barra de herramientas del visor -->
                <div class="p-2 bg-gray-100 border-b rounded-t text-right">
                    <div class="inline-flex items-center space-x-2 text-sm text-gray-600">
                    <button
                        @click="openInNewTab"
                        class="inline-flex p-1 text-gray-500 hover:text-gray-700"
                        title="Abrir en nueva pestaña"
                    >
                        Abrir en nueva pestaña
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                        </svg>
                    </button>
                    </div>
                </div>
                
                <!-- Contenedor del PDF -->
                <div class="relative bg-white">
                    <!-- Iframe para escritorio -->
                    <iframe
                    v-if="!isMobile && !showMobileFallback"
                    :src="pdfViewerUrl"
                    class="w-full border-0 rounded-b"
                    :style="{ height: '530px' }"
                    frameborder="0"
                    @load="onIframeLoad"
                    @error="onIframeError"
                    allow="autoplay; encrypted-media"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    ></iframe>
                    
                    <!-- Vista para móviles y otros documentos -->
                    <div
                    v-else-if="isMobile && !showMobileFallback"
                    class="w-full rounded-b bg-gray-50 flex items-center justify-center"
                    :style="{ height: '400px' }"
                    >
                    <!-- Intentar mostrar con object primero -->
                    <object
                        :data="currentDocumentUrl"
                        type="application/pdf"
                        class="w-full h-full"
                        @error="onEmbedError"
                    >
                        <!-- Si object falla, mostrar botones de acción -->
                        <div class="flex flex-col items-center justify-center h-full p-4">
                        <svg class="w-16 h-16 text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <p class="text-gray-600 text-center mb-4">Documento disponible</p>
                        <button
                            @click="openInNewTab"
                            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Abrir documento
                        </button>
                        </div>
                    </object>
                    </div>
                    
                    <!-- Fallback para cuando no funciona la vista previa -->
                    <div v-if="showMobileFallback" class="bg-white flex flex-col items-center justify-center p-8" :style="{ height: isMobile ? '400px' : '530px' }">
                    <svg class="w-16 h-16 text-blue-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    <p class="text-gray-700 text-center mb-2 font-medium">
                        Documento listo para visualizar
                    </p>
                    <p class="text-gray-500 text-center mb-4 text-sm">
                        {{ currentDocumentName }}
                    </p>
                    <div class="flex flex-col space-y-2 w-full max-w-xs">
                        <button
                        @click="openInNewTab"
                        class="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                        Abrir documento
                        </button>
                        <button
                        @click="downloadCurrentDocument"
                        class="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                        Descargar archivo
                        </button>
                    </div>
                    </div>
                </div>
                </div>
            </div>
            </div>
        </div>
        <!-- Mensajes de estado -->
        <div v-if="successMessage" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {{ successMessage }}
        </div>

        <div v-if="warningMessage" class="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          {{ warningMessage }}
        </div>

        <div v-if="errorMessage" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {{ errorMessage }}
        </div>

        <!-- Botón cerrar -->
        <div class="flex justify-end">
          <button
            @click="closeModal"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCareerFiles } from '@/services/api';

export default {
  name: 'DocumentsModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    selectedSubject: {
      type: Object,
      default: null
    },
    studentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      availableDocuments: [],
      loadingDocuments: false,
      documentsError: null,
      loadingDocument: false,
      successMessage: '',
      warningMessage: '',
      errorMessage: '',
      
      // Visor de documentos
      viewingDocument: false,
      currentDocumentUrl: '',
      currentDocumentName: '',
      currentDocumentId: null,
      loadingViewer: false,
      viewerError: null,
      showMobileFallback: false,
      iframeLoaded: false
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
    pdfViewerUrl() {
      if (!this.currentDocumentUrl) return '';
      
      // Para navegadores que soportan visor PDF nativo
      if (this.isMobile) {
        // En móviles, usar directamente la URL con parámetros
        return `https://docs.google.com/gview?url=${encodeURIComponent(this.currentDocumentUrl)}&embedded=true#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;
      } else {
        // En escritorio, primero intentar la URL directa, luego Google Docs como fallback
        return `https://docs.google.com/gview?url=${encodeURIComponent(this.currentDocumentUrl)}&embedded=true`;
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal && this.selectedSubject) {
        this.loadDocuments();
      } else if (!newVal) {
        this.resetModal();
      }
    }
  },
  methods: {
    async loadDocuments() {
      this.loadingDocuments = true;
      this.documentsError = null;
      
      try {
        const response = await getCareerFiles({
          idstudent: this.studentId,
          codesubject: this.selectedSubject?.codesubject,
          iddocument: 0
        });
        
        this.availableDocuments = response.data || [];
      } catch (error) {
        console.error('Error cargando documentos:', error);
        this.documentsError = 'Error al cargar los documentos. Por favor, intente nuevamente.';
      } finally {
        this.loadingDocuments = false;
      }
    },
    
    async viewDocument(documentId, documentName) {
      this.loadingViewer = true;
      this.viewerError = null;
      this.currentDocumentName = documentName;
      this.currentDocumentId = documentId;
      this.viewingDocument = true;
      this.showMobileFallback = false;
      this.iframeLoaded = false;
      
      try {
        const response = await getCareerFiles({
          idstudent: this.studentId,
          codesubject: this.selectedSubject?.codesubject,
          iddocument: documentId
        });
        
        if (response.data && response.data[0] && response.data[0].medialink) {
          this.currentDocumentUrl = response.data[0].medialink;
        } else {
          this.viewerError = 'No se pudo obtener la URL del documento.';
        }
      } catch (error) {
        console.error('Error obteniendo documento:', error);
        this.viewerError = 'Error al cargar el documento. Por favor, intente nuevamente.';
      } finally {
        this.loadingViewer = false;
      }
    },
    
    async downloadDocument(documentId, documentName) {
      this.loadingDocument = true;
      this.clearMessages();
      
      try {
        const response = await getCareerFiles({
          idstudent: this.studentId,
          codesubject: this.selectedSubject?.codesubject,
          iddocument: documentId
        });
        
        if (response.data && response.data[0] && response.data[0].medialink) {
          this.initiateDownload(response.data[0].medialink, documentName);
        } else {
          this.errorMessage = 'No se pudo obtener la URL de descarga.';
        }
      } catch (error) {
        console.error('Error descargando documento:', error);
        this.errorMessage = 'Error al descargar el documento. Por favor, intente nuevamente.';
      } finally {
        this.loadingDocument = false;
      }
    },
    
    downloadCurrentDocument() {
      if (this.currentDocumentUrl && this.currentDocumentName) {
        this.initiateDownload(this.currentDocumentUrl, this.currentDocumentName);
      }
    },
    
    initiateDownload(url, filename) {
      try {
        // Crear un enlace temporal para descargar
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Añadir al DOM temporalmente
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.successMessage = `Descarga de "${filename}" iniciada.`;
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        console.error('Error iniciando descarga:', error);
        this.errorMessage = 'Error al iniciar la descarga.';
      }
    },
    
    openInNewTab() {
      if (this.currentDocumentUrl) {
        window.open(`https://docs.google.com/gview?url=${encodeURIComponent(this.currentDocumentUrl)}&embedded=true`, '_blank', 'noopener,noreferrer');
      }
    },
    
    onIframeLoad() {
      this.iframeLoaded = true;
    },
    
    onIframeError() {
      console.log('Error cargando iframe, intentando con Google Docs viewer');
      // Si falla la URL directa, intentar con Google Docs viewer
      this.tryGoogleDocsViewer();
    },
    
    tryGoogleDocsViewer() {
      if (this.currentDocumentUrl && !this.isMobile) {
        const iframe = this.$el.querySelector('iframe');
        if (iframe) {
          const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(this.currentDocumentUrl)}&embedded=true`;
          iframe.src = googleDocsUrl;
          
          // Si Google Docs también falla, mostrar fallback
          iframe.onerror = () => {
            console.log('Google Docs viewer también falló');
            this.showMobileFallback = true;
          };
        }
      } else {
        this.showMobileFallback = true;
      }
    },
    
    onEmbedError() {
      console.log('Error cargando object, mostrando fallback');
      this.showMobileFallback = true;
    },
    
    isValidPdfUrl(url) {
      if (!url) return false;
      
      // Verificar que la URL tenga una extensión PDF o contenga indicadores de PDF
      const pdfPattern = /\.(pdf)(\?.*)?$/i;
      const hasValidExtension = pdfPattern.test(url);
      const isDataUrl = url.startsWith('data:application/pdf');
      const hasPdfInPath = url.toLowerCase().includes('.pdf');
      
      return hasValidExtension || isDataUrl || hasPdfInPath;
    },
    
    closeViewer() {
      this.viewingDocument = false;
      this.currentDocumentUrl = '';
      this.currentDocumentName = '';
      this.currentDocumentId = null;
      this.loadingViewer = false;
      this.viewerError = null;
      this.showMobileFallback = false;
      this.iframeLoaded = false;
    },
    
    closeModal() {
      this.$emit('close');
    },
    
    resetModal() {
      this.availableDocuments = [];
      this.loadingDocuments = false;
      this.documentsError = null;
      this.loadingDocument = false;
      this.clearMessages();
      this.closeViewer();
    },
    
    clearMessages() {
      this.successMessage = '';
      this.warningMessage = '';
      this.errorMessage = '';
    },
    
    formatSubject(subjectName) {
      return subjectName ? subjectName.trim() : '';
    }
  }
};
</script>